import React, { useState, useMemo } from 'react';
import { graphql } from 'gatsby';
import Layout from '../components/Layout';
import PageBanner from '../components/PageBanner';
import Section from '../components/Section';
import { Container, Row, Column } from '../components/Grid';
import Image from '../components/Image';
import Modal from '../components/Modal';
import Video from '../components/Video';
import AboutPartners from '../components/AboutPartners';
import testimonies from '../data/testimonies.json';
import './testimony.scss';

// ── Filter constants ──────────────────────────────────────────────────────────

const TOPICS = [
  'Immigration and Emigration',
  'Reparations and Healing',
  'Responding to Injustice',
  'Rights and Responsibilities',
  'Us Vs. Them',
];

const CONFLICTS = [
  'Armenian Genocide',
  'Cambodian Genocide',
  'Japanese American Internment',
  'Nanjing Massacre',
  'Rwandan Genocide',
  'The Holocaust',
];

const GRADE_BANDS = [
  { label: 'Grades 6–8 (Middle School)', value: 'Middle School' },
  { label: 'Grades 9–12 (High School)', value: 'High School' },
];

// ── Small components ──────────────────────────────────────────────────────────

const InfoIcon = ({ onClick }) => (
  <button
    className="testimony-card__info-btn"
    type="button"
    aria-label="Show bio"
    onClick={onClick}
  >
    <svg
      className="testimony-card__info-icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 20 20"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="10" cy="10" r="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
      <text x="10" y="15" textAnchor="middle" fontSize="12" fill="currentColor" fontWeight="bold">i</text>
    </svg>
  </button>
);

const TestimonyCard = ({ testimony }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="testimony-card">
      <Modal
        video
        trigger={
          <button
            className="testimony-card__trigger"
            aria-label={`Watch ${testimony.name}'s testimony`}
          >
            <div className="testimony-card__photo">
              <Image filename={testimony.thumbnail} alt="" />
              <span className="testimony-card__play-icon" aria-hidden="true">
                &#9654;
              </span>
            </div>
          </button>
        }
      >
        <Video
          src={testimony.videoSrc}
          title={`${testimony.name} Testimony`}
        />
      </Modal>
      <div className="testimony-card__caption">
        <div className="testimony-card__name-row">
          <span className="testimony-card__name">{testimony.name}</span>
          {testimony.bio && (
            <div className="testimony-card__info-wrap">
              <InfoIcon onClick={() => setShowTooltip(!showTooltip)} />
              {showTooltip && (
                <div
                  className="testimony-card__tooltip"
                  role="tooltip"
                  onClick={() => setShowTooltip(false)}
                >
                  {testimony.bio}
                </div>
              )}
            </div>
          )}
        </div>
        {(testimony.bornYear || testimony.bornLocation) && (
          <p className="testimony-card__born">
            Born {testimony.bornYear}
            {testimony.bornLocation ? `, ${testimony.bornLocation}` : ''}
          </p>
        )}
        {testimony.gradeLevel && (
          <span
            className={`testimony-card__grade testimony-card__grade--${
              testimony.gradeLevel === 'High School' ? 'hs' : 'ms'
            }`}
          >
            {testimony.gradeLevel === 'High School' ? 'High School Activity' : 'Middle School Activity'}
          </span>
        )}
      </div>
    </div>
  );
};

// ── Filter bar ────────────────────────────────────────────────────────────────

const FilterBar = ({ gradeBand, setGradeBand, conflict, setConflict, activeTopic, setActiveTopic, onReset }) => (
  <div className="testimony-filters">
    <div className="testimony-filters__dropdowns">
      {/* Grade Band dropdown */}
      <div className="testimony-filters__select-wrap">
        <label htmlFor="filter-grade" className="testimony-filters__label">
          Grade Band
        </label>
        <select
          id="filter-grade"
          className="testimony-filters__select"
          value={gradeBand}
          onChange={(e) => setGradeBand(e.target.value)}
        >
          <option value="">All Grade Bands</option>
          {GRADE_BANDS.map((g) => (
            <option key={g.value} value={g.value}>
              {g.label}
            </option>
          ))}
        </select>
      </div>

      {/* Conflict dropdown */}
      <div className="testimony-filters__select-wrap">
        <label htmlFor="filter-conflict" className="testimony-filters__label">
          Conflict
        </label>
        <select
          id="filter-conflict"
          className="testimony-filters__select"
          value={conflict}
          onChange={(e) => setConflict(e.target.value)}
        >
          <option value="">All Conflicts</option>
          {CONFLICTS.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>
    </div>

    {/* Topic pills */}
    <div className="testimony-filters__topics" role="group" aria-label="Filter by topic">
      {TOPICS.map((topic) => (
        <button
          key={topic}
          type="button"
          className={`testimony-filters__topic-btn${activeTopic === topic ? ' testimony-filters__topic-btn--active' : ''}`}
          onClick={() => setActiveTopic(activeTopic === topic ? '' : topic)}
          aria-pressed={activeTopic === topic}
        >
          {topic}
        </button>
      ))}
    </div>

    {/* Reset */}
    {(gradeBand || conflict || activeTopic) && (
      <button type="button" className="testimony-filters__reset" onClick={onReset}>
        Reset Filters
      </button>
    )}
  </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const TestimonyPage = ({ data }) => {
  const page = data.pagesYaml;

  const [gradeBand, setGradeBand] = useState('');
  const [conflict, setConflict] = useState('');
  const [activeTopic, setActiveTopic] = useState('');

  const filtered = useMemo(() => {
    return testimonies.filter((t) => {
      if (gradeBand && t.gradeLevel !== gradeBand) return false;
      if (conflict && t.conflict !== conflict) return false;
      if (activeTopic && t.activityTitle !== activeTopic) return false;
      return true;
    });
  }, [gradeBand, conflict, activeTopic]);

  const handleReset = () => {
    setGradeBand('');
    setConflict('');
    setActiveTopic('');
  };

  return (
    <Layout title={page.title} description={page.metaDescription}>
      <PageBanner title={page.title} />

      <Section>
        <Container>
          <Row>
            <Column>
              <p>
                View powerful testimony from survivors and witnesses of genocides in this
                curated library. To extend the learning, download the paired classroom
                activities geared toward middle and high school students.
              </p>
              <p>
                Testimonies appear in alphabetical order by first name. Use the filters
                below to refine results by grade band, conflict, or activity topic.
              </p>
            </Column>
          </Row>

          <FilterBar
            gradeBand={gradeBand}
            setGradeBand={setGradeBand}
            conflict={conflict}
            setConflict={setConflict}
            activeTopic={activeTopic}
            setActiveTopic={setActiveTopic}
            onReset={handleReset}
          />

          {filtered.length === 0 ? (
            <p className="testimony-no-results">No testimonies match the selected filters.</p>
          ) : (
            <div className="testimony-grid">
              {filtered.map((testimony, i) => (
                <TestimonyCard key={`${testimony.name}-${i}`} testimony={testimony} />
              ))}
            </div>
          )}
        </Container>
      </Section>

      <AboutPartners />
    </Layout>
  );
};

export default TestimonyPage;

export const query = graphql`
  query TestimonyPageQuery($id: String!) {
    pagesYaml(id: { eq: $id }) {
      slug
      path
      title
      metaDescription
    }
  }
`;
